import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../../services/api";

const numberFields = [
  "durationNights",
  "durationDays",
  "mysoreNights",
  "coorgNights",
  "kidPerPersonAgeThreshold",
  "room2Price",
  "room3Price",
  "room4Price",
  "extraMattressPrice",
  "coorgBreakfastPerPerson",
  "jeepBasePrice",
  "jeepSevenPrice",
  "jeepEightPlusPrice",
  "jeepSubtractAmount",
  "profitValue",
];

// 1 to 10 nights, so longer trips such as Manali & Shimla (5 nights) can be priced.
const durationOptions = Array.from({ length: 10 }, (_, index) => ({
  label: `${index + 1} ${index === 0 ? "Night" : "Nights"} & ${index + 2} Days`,
  nights: index + 1,
  days: index + 2,
}));

export default function DynamicPricingManager() {
  const [trips, setTrips] = useState([]);
  const [tripId, setTripId] = useState("");
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/api/admin/trips");
      const list = Array.isArray(data) ? data : [];
      setTrips(list);
      if (list.length) setTripId(String(list[0].id));
    })();
  }, []);

  useEffect(() => {
    if (!tripId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/api/admin/dynamic-pricing/${tripId}`);
        const trip = trips.find((item) => String(item.id) === String(tripId));
        setForm(withTripDuration(normalizeConfig(data), trip));
      } finally {
        setLoading(false);
      }
    })();
  }, [tripId, trips]);

  const selectedTrip = useMemo(
    () => trips.find((trip) => String(trip.id) === String(tripId)),
    [trips, tripId],
  );

  const groupedTrips = useMemo(() => {
    const groups = new Map();
    trips.forEach((trip) => {
      const key = String(trip.categoryId ?? "uncategorized");
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          name: trip.categoryName || "Uncategorized",
          trips: [],
        });
      }
      groups.get(key).trips.push(trip);
    });
    return Array.from(groups.values()).map((group) => ({
      ...group,
      trips: [...group.trips].sort((a, b) =>
        (a.title || "").localeCompare(b.title || ""),
      ),
    }));
  }, [trips]);

  const patch = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: numberFields.includes(field) ? Number(value || 0) : value,
    }));
  };

  const updateDuration = (value) => {
    const selected = durationOptions.find(
      (option) => `${option.nights}-${option.days}` === value,
    );
    if (!selected) return;
    setForm((prev) => ({
      ...prev,
      durationNights: selected.nights,
      durationDays: selected.days,
      roomRates: buildRoomRows(selected.nights, prev),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const firstRoom = (form.roomRates || [])[0] || {};
      const pickups = (form.pickups || []).map((pickup) => ({
        ...pickup,
        pickupName: (pickup.pickupName || "").trim(),
        pickupValue: (pickup.pickupValue || "").trim(),
        pricingType: pickup.pricingType || "KM_PER_DAY",
        kmPerDay: isFixedPricePickup(pickup) ? null : Number(pickup.kmPerDay || 0),
        // Only active rooms can be offered; the server refuses an inactive one.
        refreshmentRoomIds: (pickup.refreshmentRoomIds || [])
          .filter((id) => (form.availableRefreshmentRooms || []).some((room) => String(room.id) === String(id) && room.active !== false))
          .map(Number),
      }));
      const invalidPickup = pickups.find((pickup) => !pickup.pickupName || !pickup.pickupValue);
      if (invalidPickup) {
        toast.error("Pickup name and value are required before saving dynamic pricing");
        return;
      }
      const pickupValues = new Set();
      const duplicatePickup = pickups.find((pickup) => {
        const value = pickup.pickupValue.toUpperCase();
        if (pickupValues.has(value)) return true;
        pickupValues.add(value);
        return false;
      });
      if (duplicatePickup) {
        toast.error(`Duplicate pickup value: ${duplicatePickup.pickupValue}`);
        return;
      }
      const matchesSubmittedPickup = (vehicle) =>
        pickups.some((item) => {
          if (vehicle.pickupId && item.id) return String(vehicle.pickupId) === String(item.id);
          return vehicle.pickupValue && item.pickupValue && String(vehicle.pickupValue).trim().toUpperCase() === String(item.pickupValue).trim().toUpperCase();
        });
      const vehicles = (form.vehicles || [])
        .filter((vehicle) => {
          if (!vehicle.pickupId && !vehicle.pickupValue) return true;
          return matchesSubmittedPickup(vehicle);
        })
        .map((vehicle) => {
        const pickup = pickups.find((item) => {
          if (vehicle.pickupId && item.id) return String(vehicle.pickupId) === String(item.id);
          return vehicle.pickupValue && item.pickupValue && String(vehicle.pickupValue).trim().toUpperCase() === String(item.pickupValue).trim().toUpperCase();
        });
        return isFixedPricePickup(pickup)
          ? {
              ...vehicle,
              rateNonAc: 0,
              rateAc: null,
              fixedCost: Number(vehicle.fixedCost || 0),
            }
          : {
              ...vehicle,
              rateNonAc: Number(vehicle.rateNonAc || 0),
              rateAc: vehicle.rateAc === "" || vehicle.rateAc == null ? null : Number(vehicle.rateAc || 0),
              fixedCost: null,
            };
      });
      const availableAddonById = new Map(
        (form.availableAddons || [])
          .filter((addon) => addon.id != null)
          .map((addon) => [String(addon.id), addon]),
      );
      const addons = (form.addons || [])
        .filter((addon) => addon.id != null && availableAddonById.has(String(addon.id)))
        .map((addon, index) => ({
          id: Number(addon.id),
          displayOrder: addon.displayOrder ?? index + 1,
          active: addon.active !== false,
          dayNumber: Number(addon.dayNumber) >= 1 ? Number(addon.dayNumber) : null,
        }));
      const availableEpassIds = new Set((form.availableEpasses || []).map((epass) => String(epass.id)));
      const epasses = (form.epasses || [])
        .filter((epass) => epass.id != null && availableEpassIds.has(String(epass.id)))
        .map((epass) => ({
          id: Number(epass.id),
          dayNumber: Number(epass.dayNumber) >= 1 ? Number(epass.dayNumber) : null,
        }));
      const { data } = await api.put(`/api/admin/dynamic-pricing/${tripId}`, {
        ...form,
        tripId: Number(tripId),
        pickups,
        vehicles,
        addons,
        epasses,
        mysoreNights: Number(form.durationNights || 0),
        coorgNights: 0,
        room2Price: Number(firstRoom.room2Price ?? form.room2Price ?? 0),
        room3Price: Number(firstRoom.room3Price ?? form.room3Price ?? 0),
        room4Price: Number(firstRoom.room4Price ?? form.room4Price ?? 0),
        extraMattressPrice: Number(firstRoom.extraMattressPrice ?? form.extraMattressPrice ?? 0),
        kidRoomCountAgeThreshold: Number(firstRoom.kidRoomCountAgeThreshold ?? form.kidRoomCountAgeThreshold ?? 3),
      });
      setForm(normalizeConfig(data));
      toast.success("Dynamic pricing saved");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to save dynamic pricing";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Card className="shadow-sm">
      <Card.Header className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <div>
          <div className="fw-semibold">Dynamic Trip Pricing</div>
          <div className="text-muted small">
            Configure rooms, vehicles, pickup pricing, optional add-ons, profit, and rounding.
          </div>
        </div>
        <Form.Select
          size="sm"
          style={{ maxWidth: 360 }}
          value={tripId}
          onChange={(event) => setTripId(event.target.value)}
        >
          {groupedTrips.map((group) => (
            <optgroup key={group.key} label={group.name}>
              {group.trips.map((trip) => (
                <option key={trip.id} value={trip.id}>
                  {trip.title}
                </option>
              ))}
            </optgroup>
          ))}
        </Form.Select>
      </Card.Header>

      <Card.Body>
        {loading || !form ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : (
          <>
            <Alert variant={form.enabled ? "success" : "secondary"}>
              {selectedTrip?.title || form.tripTitle || "Selected trip"} dynamic pricing is{" "}
              <strong>{form.enabled ? "enabled" : "disabled"}</strong>.
            </Alert>

            <Accordion defaultActiveKey={["setup", "rooms"]} alwaysOpen>
              <Accordion.Item eventKey="setup">
                <Accordion.Header>Trip Setup</Accordion.Header>
                <Accordion.Body>
                  <Row className="g-3">
                    <Col md={3}>
                      <Form.Check
                        type="switch"
                        label="Enable dynamic pricing"
                        checked={!!form.enabled}
                        onChange={(e) => patch("enabled", e.target.checked)}
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Label>Trip Duration</Form.Label>
                      <Form.Select
                        value={`${form.durationNights || 1}-${form.durationDays || 2}`}
                        onChange={(e) => updateDuration(e.target.value)}
                      >
                        {durationOptions.map((option) => (
                          <option
                            key={`${option.nights}-${option.days}`}
                            value={`${option.nights}-${option.days}`}
                          >
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Form.Label>Kid per-person age threshold (this age and under not charged)</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          value={form.kidPerPersonAgeThreshold ?? 3}
                          onChange={(e) => patch("kidPerPersonAgeThreshold", e.target.value)}
                        />
                        <InputGroup.Text>years</InputGroup.Text>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Kids older than this age are included in per-person pricing.
                      </Form.Text>
                    </Col>
                    <Col md={12}>
                      <Form.Label>Refreshment Rooms by Pickup</Form.Label>
                      <PickupRefreshmentRooms
                        pickups={form.pickups || []}
                        rooms={form.availableRefreshmentRooms || []}
                        onChange={(rows) => patch("pickups", rows)}
                      />
                      <Form.Text className="text-muted">
                        Lead confirmation offers only the rooms ticked for the customer's pickup.
                      </Form.Text>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="rooms">
                <Accordion.Header>Hotel</Accordion.Header>
                <Accordion.Body>
                  <RoomRatesTable
                    rows={form.roomRates || []}
                    durationNights={form.durationNights || 1}
                    hotels={form.availableHotels || []}
                    defaults={{
                      room2Price: form.room2Price,
                      room3Price: form.room3Price,
                      room4Price: form.room4Price,
                      extraMattressPrice: form.extraMattressPrice,
                      kidRoomCountAgeThreshold: form.kidRoomCountAgeThreshold,
                    }}
                    onChange={(rows) => patch("roomRates", rows)}
                  />
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="pickups">
                <Accordion.Header>Pickup KM Rules</Accordion.Header>
                <Accordion.Body>
                  <EditablePickupTable
                    rows={form.pickups}
                    onChange={(rows) => patch("pickups", rows)}
                    onRemove={(removedPickup, rows) => {
                      setForm((prev) => ({
                        ...prev,
                        pickups: rows,
                        vehicles: (prev.vehicles || []).filter((vehicle) => !samePickupRef(vehicle, removedPickup)),
                      }));
                    }}
                  />
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="vehicles">
                <Accordion.Header>Vehicle Master</Accordion.Header>
                <Accordion.Body>
                  <PickupVehicleMasters
                    pickups={form.pickups}
                    vehicles={form.vehicles}
                    onChange={(rows) => patch("vehicles", rows)}
                  />
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="addons">
                <Accordion.Header>Add-ons</Accordion.Header>
                <Accordion.Body>
                  <TripAddonSelector
                    selected={form.addons || []}
                    available={form.availableAddons || []}
                    onChange={(addons) => patch("addons", addons)}
                  />
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="epasses">
                <Accordion.Header>E-Passes</Accordion.Header>
                <Accordion.Body>
                  <TripEpassSelector
                    selected={form.epasses || []}
                    available={form.availableEpasses || []}
                    durationDays={Number(form.durationDays || 1)}
                    onChange={(epasses) => patch("epasses", epasses)}
                  />
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="profit">
                <Accordion.Header>Profit and Rounding</Accordion.Header>
                <Accordion.Body>
                  <Row className="g-3">
                    <Col md={3}>
                      <Form.Label>Profit type</Form.Label>
                      <Form.Select value={form.profitType} onChange={(e) => patch("profitType", e.target.value)}>
                        <option value="PERCENTAGE">Percentage</option>
                        <option value="FIXED_PER_PERSON">Fixed per person</option>
                        <option value="FIXED_PER_TRIP">Fixed per trip</option>
                      </Form.Select>
                    </Col>
                    <MoneyInput label="Profit value" value={form.profitValue} onChange={(v) => patch("profitValue", v)} />
                    <Col md={3}>
                      <Form.Label>Rounding</Form.Label>
                      <Form.Select value={form.roundingType} onChange={(e) => patch("roundingType", e.target.value)}>
                        <option value="NEAREST_100">Next 100</option>
                        <option value="NEAREST_99">Next 99</option>
                        <option value="NONE">No rounding</option>
                      </Form.Select>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            <div className="d-flex justify-content-end mt-3">
              <Button onClick={save} disabled={saving}>
                {saving ? "Saving..." : "Save Dynamic Pricing"}
              </Button>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
    <QuoteBreakupCalculator trips={trips} groupedTrips={groupedTrips} initialTripId={tripId} />
    </>
  );
}

function QuoteBreakupCalculator({ trips, groupedTrips, initialTripId }) {
  const [tripId, setTripId] = useState(initialTripId || "");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (initialTripId && !tripId) setTripId(String(initialTripId));
  }, [initialTripId, tripId]);

  const selectedTrip = useMemo(
    () => trips.find((trip) => String(trip.id) === String(tripId)),
    [trips, tripId],
  );
  const path = selectedTrip?.path || selectedTrip?.tripPath || "";
  const calculatorUrl = path ? `/trips/${path}?adminCalculator=1#book-now` : "";

  return (
    <Card className="shadow-sm mt-3">
      <Card.Header className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <div>
          <div className="fw-semibold">Detailed Breakup Calculator</div>
          <div className="text-muted small">Uses the same calculator UI as the customer trip page. No booking is created here.</div>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          {expanded && (
            <Form.Select size="sm" style={{ maxWidth: 360 }} value={tripId} onChange={(event) => setTripId(event.target.value)}>
              {groupedTrips.map((group) => (
                <optgroup key={group.key} label={group.name}>
                  {group.trips.map((trip) => (
                    <option key={trip.id} value={trip.id}>{trip.title}</option>
                  ))}
                </optgroup>
              ))}
            </Form.Select>
          )}
          <Button size="sm" variant="outline-secondary" onClick={() => setExpanded((previous) => !previous)}>
            {expanded ? "Hide" : "Show"}
          </Button>
        </div>
      </Card.Header>
      {expanded && (
        <Card.Body>
          {calculatorUrl ? (
            <iframe
              title="Customer trip quote calculator preview"
              src={calculatorUrl}
              style={{ width: "100%", height: 960, border: 0, background: "#fff" }}
            />
          ) : (
            <Alert variant="warning">Selected trip does not have a public path configured.</Alert>
          )}
        </Card.Body>
      )}
    </Card>
  );
}
function normalizeConfig(data) {
  const pickups = Array.isArray(data.pickups) ? data.pickups.map(normalizePickupRow) : [];
  let vehicles = Array.isArray(data.vehicles) ? data.vehicles.map(normalizeVehicleRow) : [];
  const refreshmentRoomIds = Array.isArray(data.refreshmentRoomIds) && data.refreshmentRoomIds.length
    ? data.refreshmentRoomIds
    : data.refreshmentRoomId || data.refreshmentRoom?.id
      ? [data.refreshmentRoomId ?? data.refreshmentRoom?.id]
      : [];
  if (pickups.length && vehicles.length && vehicles.every((vehicle) => !vehicle.pickupId && !vehicle.pickupValue)) {
    vehicles = pickups.flatMap((pickup) =>
      vehicles.map((vehicle) =>
        normalizeVehicleRow({
          ...vehicle,
          id: null,
          pickupId: pickup.id,
          pickupValue: pickup.pickupValue,
          pickupName: pickup.pickupName,
        }),
      ),
    );
  }
  return {
    ...data,
    enabled: !!data.enabled,
    kidRoomCountAgeThreshold: data.kidRoomCountAgeThreshold ?? 3,
    kidPerPersonAgeThreshold: data.kidPerPersonAgeThreshold ?? 3,
    roomRates: Array.isArray(data.roomRates) ? data.roomRates : [],
    availableHotels: Array.isArray(data.availableHotels) ? data.availableHotels.map(normalizeHotelRow) : [],
    refreshmentRoomId: data.refreshmentRoomId ?? data.refreshmentRoom?.id ?? "",
    refreshmentRoomIds: refreshmentRoomIds.map(String),
    refreshmentRooms: Array.isArray(data.refreshmentRooms) ? data.refreshmentRooms.map(normalizeRefreshmentRoomRow) : [],
    refreshmentRoom: data.refreshmentRoom || null,
    availableRefreshmentRooms: Array.isArray(data.availableRefreshmentRooms) ? data.availableRefreshmentRooms.map(normalizeRefreshmentRoomRow) : [],
    epasses: Array.isArray(data.epasses) ? data.epasses : [],
    availableEpasses: Array.isArray(data.availableEpasses) ? data.availableEpasses : [],
    pickups,
    vehicles,
  };
}

function normalizeRefreshmentRoomRow(row = {}) {
  return {
    ...row,
    name: row.name || "",
    city: row.city || "",
    googleMapLink: row.googleMapLink || "",
    description: row.description || "",
    displayOrder: row.displayOrder ?? 0,
    active: row.active !== false,
  };
}

function normalizePickupRow(row = {}) {
  const pricingType = row.pricingType || "KM_PER_DAY";
  return {
    ...row,
    pickupName: row.pickupName || "",
    pickupValue: row.pickupValue || "",
    pricingType,
    kmPerDay: pricingType === "FIXED_PRICE" ? "" : row.kmPerDay ?? 0,
    displayOrder: row.displayOrder ?? 0,
    active: row.active !== false,
    refreshmentRoomIds: (row.refreshmentRoomIds || []).map(String),
  };
}

// One row per pickup with the refreshment rooms lead confirmation may offer for it.
function PickupRefreshmentRooms({ pickups, rooms, onChange }) {
  if (!pickups.length) {
    return <div className="small text-muted border rounded p-2">Add pickup options first.</div>;
  }
  if (!rooms.length) {
    return <div className="small text-muted border rounded p-2">No refreshment rooms available.</div>;
  }
  const toggle = (index, roomId, checked) => {
    onChange(pickups.map((pickup, idx) => {
      if (idx !== index) return pickup;
      const selected = new Set((pickup.refreshmentRoomIds || []).map(String));
      if (checked) selected.add(String(roomId));
      else selected.delete(String(roomId));
      return { ...pickup, refreshmentRoomIds: Array.from(selected) };
    }));
  };
  return (
    <Table size="sm" bordered className="align-middle mb-1">
      <thead>
        <tr><th style={{ width: 280 }}>Pickup</th><th>Refreshment Rooms</th></tr>
      </thead>
      <tbody>
        {pickups.map((pickup, index) => (
          <tr key={pickup.id || `new-${index}`}>
            <td>
              {pickup.pickupName || `Pickup ${index + 1}`}
              {pickup.active === false && <span className="small text-muted"> (Inactive)</span>}
            </td>
            <td>
              <div className="d-flex flex-wrap gap-3">
                {rooms.map((room) => (
                  <Form.Check
                    key={room.id}
                    type="checkbox"
                    id={`pickup-${index}-refreshment-room-${room.id}`}
                    label={`${room.name}${room.city ? ` - ${room.city}` : ""}${room.active === false ? " (Inactive)" : ""}`}
                    checked={(pickup.refreshmentRoomIds || []).map(String).includes(String(room.id))}
                    disabled={room.active === false}
                    onChange={(event) => toggle(index, room.id, event.target.checked)}
                  />
                ))}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function isFixedPricePickup(pickup = {}) {
  return pickup.pricingType === "FIXED_PRICE";
}

function samePickupRef(vehicle = {}, pickup = {}) {
  if (vehicle.pickupId && pickup.id) return String(vehicle.pickupId) === String(pickup.id);
  if (vehicle.pickupValue && pickup.pickupValue) {
    return String(vehicle.pickupValue).trim().toUpperCase() === String(pickup.pickupValue).trim().toUpperCase();
  }
  return false;
}

function normalizePickupVehicleRow(row, pickup) {
  const normalized = normalizeVehicleRow({
    ...row,
    pickupId: pickup?.id ?? row?.pickupId ?? null,
    pickupValue: pickup?.pickupValue || row?.pickupValue || "",
    pickupName: pickup?.pickupName || row?.pickupName || "",
  });
  return isFixedPricePickup(pickup)
    ? { ...normalized, rateNonAc: "", rateAc: "", fixedCost: normalized.fixedCost ?? 0 }
    : { ...normalized, fixedCost: "" };
}

function normalizeHotelRow(row = {}) {
  return {
    ...row,
    hotelName: row.hotelName || "",
    hotelCode: row.hotelCode || "",
    city: row.city || "",
    displayOrder: row.displayOrder ?? 0,
    active: row.active !== false,
    roomCategories: Array.isArray(row.roomCategories)
      ? row.roomCategories.map(normalizeHotelCategoryRow)
      : [],
  };
}

function normalizeHotelCategoryRow(row = {}) {
  return {
    ...row,
    categoryName: row.categoryName || "",
    room2Price: row.room2Price ?? 0,
    room3Price: row.room3Price ?? 0,
    room4Price: row.room4Price ?? 0,
    extraMattressPrice: row.extraMattressPrice ?? 0,
    kidRoomCountAgeThreshold: row.kidRoomCountAgeThreshold ?? 3,
    kidGracePerRoom: row.kidGracePerRoom ?? 2,
    displayOrder: row.displayOrder ?? 0,
    active: row.active !== false,
    priceOptions: Array.isArray(row.priceOptions) ? row.priceOptions : [],
  };
}

function normalizeVehicleRow(row = {}) {
  return {
    ...row,
    pickupId: row.pickupId ?? null,
    pickupValue: row.pickupValue || "",
    pickupName: row.pickupName || "",
    vehicleType: row.vehicleType || "",
    acType: row.acType || "NON_AC",
    capacityMin: 1,
    capacityMax: row.capacityMax ?? 4,
    rateNonAc: row.rateNonAc ?? 0,
    rateAc: row.rateAc ?? "",
    fixedCost: row.fixedCost ?? "",
    driverBetaPerDay: row.driverBetaPerDay ?? 0,
    tolls: row.tolls ?? 0,
    parking: row.parking ?? 0,
    childConcessionAgeLimit: row.childConcessionAgeLimit ?? 3,
    maxKidsWithoutSeat: row.maxKidsWithoutSeat ?? 0,
    displayOrder: row.displayOrder ?? 0,
    active: row.active !== false,
  };
}

// Pricing that was never saved comes back as a 2-night default; start it at the trip's own length.
// Trip durations are typed many ways ("6days-5nights", "3 Days / 2 Nights"), so the category name backs them up.
function withTripDuration(form, trip) {
  if (form.id) return form;
  const text = `${trip?.duration || ""} ${trip?.categoryName || ""}`;
  const nights = Number(text.match(/(\d+)\s*nights?/i)?.[1]);
  const days = Number(text.match(/(\d+)\s*days?/i)?.[1]);
  const option = durationOptions.find((item) => item.nights === nights && item.days === days);
  if (!option) return form;
  return {
    ...form,
    durationNights: option.nights,
    durationDays: option.days,
    roomRates: buildRoomRows(option.nights, form),
  };
}

function buildRoomRows(durationNights, source) {
  const existingRows = Array.isArray(source.roomRates) ? source.roomRates : [];
  const count = Math.max(1, Number(durationNights || 1));
  const rowsByNight = existingRows.reduce((groups, row) => {
    const night = Math.max(1, Number(row.nightNumber || 1));
    if (!groups.has(night)) groups.set(night, []);
    groups.get(night).push(row);
    return groups;
  }, new Map());
  const nextRows = [];
  for (let night = 1; night <= count; night += 1) {
    const existingNightRows = rowsByNight.get(night) || [];
    if (existingNightRows.length) {
      existingNightRows.forEach((row) => {
        nextRows.push({
          ...row,
          nightNumber: night,
          displayOrder: nextRows.length + 1,
          active: row.active !== false,
        });
      });
      continue;
    }
    const previousNightRows = rowsByNight.get(night - 1) || [];
    const existing = previousNightRows[0] || existingRows[nextRows.length] || {};
    nextRows.push({
      roomLabel: existing.roomLabel || `Room ${night}`,
      nightNumber: night,
      room2Price: existing.room2Price ?? source.room2Price ?? 0,
      room3Price: existing.room3Price ?? source.room3Price ?? 0,
      room4Price: existing.room4Price ?? source.room4Price ?? 0,
      extraMattressPrice:
        existing.extraMattressPrice ?? source.extraMattressPrice ?? 0,
      kidRoomCountAgeThreshold:
        existing.kidRoomCountAgeThreshold ?? source.kidRoomCountAgeThreshold ?? 3,
      displayOrder: nextRows.length + 1,
      active: existing.active !== false,
    });
  }
  return nextRows;
}

function MoneyInput({ label, value, onChange }) {
  return (
    <Col md={3}>
      <Form.Label>{label}</Form.Label>
      <InputGroup>
        <InputGroup.Text>Rs.</InputGroup.Text>
        <Form.Control type="number" min="0" step="0.01" value={value ?? 0} onChange={(e) => onChange(e.target.value)} />
      </InputGroup>
    </Col>
  );
}

function RoomRatesTable({ rows, durationNights, defaults, hotels, onChange }) {
  const hotelOptions = (hotels || []).filter((hotel) => hotel.active !== false && hotel.id);
  const cityOf = (hotel) => String(hotel?.city || "").trim() || "Unassigned";
  const cities = Array.from(new Set(hotelOptions.map(cityOf))).sort((a, b) => a.localeCompare(b));
  const hotelsInCity = (city) => hotelOptions
    .filter((hotel) => cityOf(hotel) === city)
    .sort((a, b) => String(a.hotelName || "").localeCompare(String(b.hotelName || "")));
  const [cityByNight, setCityByNight] = useState({});
  const nightCount = Math.max(1, Number(durationNights || 1), ...rows.map((row) => Number(row.nightNumber || 1)));
  const nights = Array.from({ length: nightCount }, (_, index) => index + 1);
  const rowsForNight = (night) => rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => Number(row.nightNumber || 1) === night);
  const cityForNight = (night) => {
    const withHotel = rowsForNight(night).find(({ row }) => row.hotelId);
    if (!withHotel) return cityByNight[night] || "";
    const hotel = hotelOptions.find((item) => String(item.id) === String(withHotel.row.hotelId));
    return hotel ? cityOf(hotel) : String(withHotel.row.hotelCity || "").trim() || "";
  };
  const update = (index, field, value) => {
    onChange(rows.map((row, idx) => (idx === index ? { ...row, [field]: value } : row)));
  };
  const rowFromHotel = (hotel, index) => ({
    hotelId: hotel.id,
    hotelName: hotel.hotelName,
    hotelCode: hotel.hotelCode,
    hotelCity: hotel.city,
    roomCategoryId: null,
    roomCategoryName: "",
    roomLabel: hotel.hotelName,
    nightNumber: index + 1,
    room2Price: defaults.room2Price ?? 0,
    room3Price: defaults.room3Price ?? 0,
    room4Price: defaults.room4Price ?? 0,
    extraMattressPrice: defaults.extraMattressPrice ?? 0,
    kidRoomCountAgeThreshold: defaults.kidRoomCountAgeThreshold ?? 3,
    priceOptions: [],
    displayOrder: index + 1,
    active: true,
  });
  const selectHotel = (index, hotelId) => {
    const hotel = hotelOptions.find((item) => String(item.id) === String(hotelId));
    if (!hotel) {
      return;
    }
    onChange(rows.map((row, idx) => idx === index
      ? { ...row, ...rowFromHotel(hotel, idx), id: row.id, nightNumber: row.nightNumber, displayOrder: row.displayOrder }
      : row));
  };
  const setCityForNight = (night, city) => {
    setCityByNight((previous) => ({ ...previous, [night]: city }));
    const keepIds = new Set(hotelsInCity(city).map((hotel) => String(hotel.id)));
    onChange(rows.filter((row) => Number(row.nightNumber || 1) !== night || keepIds.has(String(row.hotelId))));
  };
  const addHotelToNight = (night) => {
    const city = cityForNight(night);
    const used = new Set(rowsForNight(night).map(({ row }) => String(row.hotelId)));
    const hotel = (city ? hotelsInCity(city) : hotelOptions).find((item) => !used.has(String(item.id)));
    if (!hotel) return;
    onChange([...rows, { ...rowFromHotel(hotel, rows.length), nightNumber: night }]);
  };
  const hotelChoicesForRow = (city, row) => {
    const list = hotelsInCity(city);
    if (!row.hotelId || list.some((hotel) => String(hotel.id) === String(row.hotelId))) return list;
    const current = hotelOptions.find((hotel) => String(hotel.id) === String(row.hotelId));
    return [current || { id: row.hotelId, hotelName: row.hotelName || `Hotel ${row.hotelId}`, hotelCode: row.hotelCode }, ...list];
  };
  const generateRows = () => {
    const hotel = hotelOptions[0];
    if (!hotel) return;
    const count = Math.max(1, Number(durationNights || 1));
    onChange(Array.from({ length: count }, (_, index) => rowFromHotel(hotel, index)));
  };

  return (
    <>
      {!hotelOptions.length && (
        <Alert variant="warning">
          No active hotels found. Create hotels from the Hotels menu first.
        </Alert>
      )}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <div className="text-muted small">
          Pick a city for each night, then choose hotels from that city. Customers will see all active room categories under the selected hotel.
        </div>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={generateRows} disabled={!hotelOptions.length}>
            Generate {durationNights || 1} Rows
          </Button>
        </div>
      </div>
      <div className="d-flex flex-column gap-3">
        {nights.map((night) => {
          const city = cityForNight(night);
          const nightRows = rowsForNight(night);
          const cityHotels = city ? hotelsInCity(city) : [];
          const usedIds = new Set(nightRows.map(({ row }) => String(row.hotelId)));
          return (
            <Card key={night} className="border">
              <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-2">
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-semibold">Night {night}</span>
                  <Form.Select
                    size="sm"
                    style={{ minWidth: 200 }}
                    value={city}
                    disabled={!hotelOptions.length}
                    onChange={(e) => setCityForNight(night, e.target.value)}
                  >
                    <option value="">Select city</option>
                    {cities.map((item) => <option key={item} value={item}>{item}</option>)}
                  </Form.Select>
                </div>
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={!city || !cityHotels.some((hotel) => !usedIds.has(String(hotel.id)))}
                  onClick={() => addHotelToNight(night)}
                >
                  Add Hotel
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                {!city && (
                  <div className="text-center text-muted small py-3">Select a city to add hotels for this night.</div>
                )}
                {city && !nightRows.length && (
                  <div className="text-center text-muted small py-3">No hotels yet for Night {night}. Use Add Hotel.</div>
                )}
                {city && nightRows.length > 0 && (
                  <Table responsive size="sm" className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 44 }}>#</th>
                        <th>Label</th>
                        <th>Hotel</th>
                        <th>Order</th>
                        <th>Active</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {nightRows.map(({ row, index }, position) => (
                        <tr key={index}>
                          <td className="small fw-semibold">{position + 1}</td>
                          <td>
                            <Form.Control size="sm" value={row.roomLabel || ""} onChange={(e) => update(index, "roomLabel", e.target.value)} />
                          </td>
                          <td style={{ minWidth: 240 }}>
                            <Form.Select size="sm" value={row.hotelId || ""} onChange={(e) => selectHotel(index, e.target.value)}>
                              <option value="" disabled>Select hotel</option>
                              {hotelChoicesForRow(city, row).map((hotel) => (
                                <option
                                  key={hotel.id}
                                  value={hotel.id}
                                  disabled={usedIds.has(String(hotel.id)) && String(hotel.id) !== String(row.hotelId)}
                                >
                                  {hotel.hotelName}{hotel.hotelCode ? ` (${hotel.hotelCode})` : ""}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td style={{ width: 110 }}>
                            <Form.Control size="sm" type="number" value={row.displayOrder || 0} onChange={(e) => update(index, "displayOrder", Number(e.target.value || 0))} />
                          </td>
                          <td style={{ width: 70 }}>
                            <Form.Check checked={row.active !== false} onChange={(e) => update(index, "active", e.target.checked)} />
                          </td>
                          <td className="text-end" style={{ width: 100 }}>
                            <Button size="sm" variant="outline-danger" onClick={() => onChange(rows.filter((_, idx) => idx !== index))}>
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          );
        })}
      </div>
    </>
  );
}

function EditablePickupTable({ rows, onChange, onRemove }) {
  const update = (index, field, value) => {
    onChange(rows.map((row, idx) => {
      if (idx !== index) return row;
      if (field === "pricingType") {
        return {
          ...row,
          pricingType: value,
          kmPerDay: value === "FIXED_PRICE" ? "" : row.kmPerDay ?? 0,
        };
      }
      return { ...row, [field]: value };
    }));
  };
  const remove = (index) => {
    const nextRows = rows.filter((_, idx) => idx !== index);
    if (onRemove) {
      onRemove(rows[index], nextRows);
      return;
    }
    onChange(nextRows);
  };
  return (
    <>
      <Table responsive size="sm" className="align-middle">
        <thead>
          <tr><th style={{ width: 44 }}>#</th><th>Name</th><th>Value</th><th>Type</th><th>KM/day</th><th>Order</th><th>Active</th><th /></tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}><td className="small fw-semibold">{index + 1}</td>
              <td><Form.Control value={row.pickupName || ""} onChange={(e) => update(index, "pickupName", e.target.value)} /></td>
              <td><Form.Control value={row.pickupValue || ""} onChange={(e) => update(index, "pickupValue", e.target.value)} /></td>
              <td>
                <Form.Select value={row.pricingType || "KM_PER_DAY"} onChange={(e) => update(index, "pricingType", e.target.value)}>
                  <option value="KM_PER_DAY">KM / day</option>
                  <option value="FIXED_PRICE">Fixed price</option>
                </Form.Select>
              </td>
              <td>
                {isFixedPricePickup(row) ? (
                  <span className="text-muted small">Not required</span>
                ) : (
                  <Form.Control type="number" min="0" step="0.01" value={row.kmPerDay ?? 0} onChange={(e) => update(index, "kmPerDay", Number(e.target.value || 0))} />
                )}
              </td>
              <td><Form.Control type="number" value={row.displayOrder || 0} onChange={(e) => update(index, "displayOrder", Number(e.target.value || 0))} /></td>
              <td><Form.Check checked={row.active !== false} onChange={(e) => update(index, "active", e.target.checked)} /></td>
              <td><Button size="sm" variant="outline-danger" onClick={() => remove(index)}>Remove</Button></td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Button size="sm" variant="outline-primary" onClick={() => onChange([...rows, { pickupName: "", pickupValue: "", pricingType: "KM_PER_DAY", kmPerDay: 0, displayOrder: rows.length + 1, active: true, refreshmentRoomIds: [] }])}>
        Add Pickup
      </Button>
    </>
  );
}

function PickupVehicleMasters({ pickups, vehicles, onChange }) {
  const activePickups = (pickups || []).filter((pickup) => pickup.active !== false);
  const matchesPickup = (vehicle, pickup) => {
    if (vehicle.pickupId && pickup.id) return String(vehicle.pickupId) === String(pickup.id);
    if (vehicle.pickupValue && pickup.pickupValue) return String(vehicle.pickupValue) === String(pickup.pickupValue);
    return false;
  };
  const setPickupRows = (pickup, rows) => {
    const others = (vehicles || []).filter((vehicle) => !matchesPickup(vehicle, pickup));
    onChange([...others, ...rows]);
  };

  if (!activePickups.length) {
    return <Alert variant="warning">Add pickup options before configuring pickup-wise vehicle masters.</Alert>;
  }

  return (
    <Accordion alwaysOpen defaultActiveKey={activePickups[0]?.pickupValue || "pickup-0"}>
      {activePickups.map((pickup, index) => {
        const rows = (vehicles || []).filter((vehicle) => matchesPickup(vehicle, pickup)).map((vehicle) => normalizePickupVehicleRow(vehicle, pickup));
        const eventKey = pickup.pickupValue || `pickup-${index}`;
        return (
          <Accordion.Item eventKey={eventKey} key={`${pickup.id || pickup.pickupValue || index}`}>
            <Accordion.Header>
              {pickup.pickupName || `Pickup ${index + 1}`} Vehicle Master
            </Accordion.Header>
            <Accordion.Body>
              <EditableVehicleTable
                pickup={pickup}
                rows={rows}
                onChange={(nextRows) => setPickupRows(pickup, nextRows)}
              />
            </Accordion.Body>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}

function EditableVehicleTable({ pickup, rows, onChange }) {
  const fixedPrice = isFixedPricePickup(pickup);
  const update = (index, field, value) => {
    onChange(rows.map((row, idx) => (idx === index ? { ...row, [field]: value } : row)));
  };
  const addVehicle = () => {
    onChange([
      ...rows,
      normalizeVehicleRow({
        pickupId: pickup?.id ?? null,
        pickupValue: pickup?.pickupValue || "",
        pickupName: pickup?.pickupName || "",
        vehicleName: "",
        vehicleType: "",
        capacityMax: 4,
        acType: "NON_AC",
        rateNonAc: fixedPrice ? "" : 0,
        rateAc: "",
        fixedCost: fixedPrice ? 0 : "",
        driverBetaPerDay: 0,
        tolls: 0,
        parking: 0,
        childConcessionAgeLimit: 3,
        maxKidsWithoutSeat: 0,
        displayOrder: rows.length + 1,
        active: true,
      }),
    ]);
  };
  return (
    <>
      <div className="d-flex flex-column gap-3">
        {rows.map((rawRow, index) => {
          const row = normalizeVehicleRow(rawRow);
          return (
            <div key={index} className="border rounded p-3 bg-light">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div className="fw-semibold">Vehicle #{index + 1}</div>
                <div className="d-flex align-items-center gap-3">
                  <Form.Check
                    type="switch"
                    id={`vehicle-active-${index}`}
                    label="Active"
                    checked={row.active !== false}
                    onChange={(e) => update(index, "active", e.target.checked)}
                  />
                  <Button size="sm" variant="outline-danger" onClick={() => onChange(rows.filter((_, idx) => idx !== index))}>
                    Remove
                  </Button>
                </div>
              </div>

              <Row className="g-3">
                <Col lg={4} md={6}>
                  <Form.Label>Vehicle Name</Form.Label>
                  <Form.Control value={row.vehicleName || ""} onChange={(e) => update(index, "vehicleName", e.target.value)} />
                </Col>
                <Col lg={3} md={6}>
                  <Form.Label>Vehicle Type</Form.Label>
                  <Form.Control value={row.vehicleType || ""} onChange={(e) => update(index, "vehicleType", e.target.value)} placeholder="SEDAN / TEMPO / BUS" />
                </Col>
                <Col lg={2} md={4}>
                  <Form.Label>Max Capacity (inclusive)</Form.Label>
                  <Form.Control type="number" min="0" step="1" value={row.capacityMax} onChange={(e) => update(index, "capacityMax", Number(e.target.value || 0))} />
                </Col>
                <Col lg={1} md={4}>
                  <Form.Label>Order</Form.Label>
                  <Form.Control type="number" min="0" step="1" value={row.displayOrder} onChange={(e) => update(index, "displayOrder", Number(e.target.value || 0))} />
                </Col>

                {fixedPrice ? (
                  <Col lg={3} md={4}>
                    <Form.Label>Fixed Cost</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>Rs.</InputGroup.Text>
                      <Form.Control type="number" min="0" step="0.01" value={row.fixedCost ?? 0} onChange={(e) => update(index, "fixedCost", Number(e.target.value || 0))} />
                    </InputGroup>
                  </Col>
                ) : (
                  <>
                    <Col lg={3} md={4}>
                      <Form.Label>AC / Non-AC</Form.Label>
                      <Form.Select value={row.acType || "NON_AC"} onChange={(e) => update(index, "acType", e.target.value)}>
                        <option value="NON_AC">Non-AC</option>
                        <option value="AC">AC</option>
                        <option value="BOTH">Both</option>
                      </Form.Select>
                    </Col>
                    <Col lg={3} md={4}>
                      <Form.Label>Non-AC Rate / KM</Form.Label>
                      <Form.Control type="number" min="0" step="0.01" value={row.rateNonAc} onChange={(e) => update(index, "rateNonAc", Number(e.target.value || 0))} />
                    </Col>
                    <Col lg={3} md={4}>
                      <Form.Label>AC Rate / KM</Form.Label>
                      <Form.Control type="number" min="0" step="0.01" value={row.rateAc} onChange={(e) => update(index, "rateAc", e.target.value === "" ? "" : Number(e.target.value || 0))} />
                    </Col>
                  </>
                )}
                <Col lg={3} md={4}>
                  <Form.Label>Driver Beta / Day</Form.Label>
                  <Form.Control type="number" min="0" step="0.01" value={row.driverBetaPerDay} onChange={(e) => update(index, "driverBetaPerDay", Number(e.target.value || 0))} />
                </Col>

                <Col lg={3} md={4}>
                  <Form.Label>Tolls</Form.Label>
                  <Form.Control type="number" min="0" step="0.01" value={row.tolls} onChange={(e) => update(index, "tolls", Number(e.target.value || 0))} />
                </Col>
                <Col lg={3} md={4}>
                  <Form.Label>Parking</Form.Label>
                  <Form.Control type="number" min="0" step="0.01" value={row.parking} onChange={(e) => update(index, "parking", Number(e.target.value || 0))} />
                </Col>
                <Col lg={3} md={4}>
                  <Form.Label>Child Concession Age Limit (this age and under)</Form.Label>
                  <Form.Control type="number" min="0" step="1" value={row.childConcessionAgeLimit} onChange={(e) => update(index, "childConcessionAgeLimit", Number(e.target.value || 0))} />
                </Col>
                <Col lg={3} md={4}>
                  <Form.Label>Max Kids Without Seat (inclusive)</Form.Label>
                  <Form.Control type="number" min="0" step="1" value={row.maxKidsWithoutSeat} onChange={(e) => update(index, "maxKidsWithoutSeat", Number(e.target.value || 0))} />
                </Col>
              </Row>
            </div>
          );
        })}
      </div>
      <Button size="sm" variant="outline-primary" onClick={addVehicle}>
        Add Vehicle
      </Button>
    </>
  );
}

function TripAddonSelector({ selected, available, onChange }) {
  const availableIds = new Set((available || []).map((addon) => addon.id));
  const selectedIds = new Set(
    (selected || [])
      .map((addon) => addon.id)
      .filter((id) => availableIds.has(id)),
  );
  const toggle = (addon, checked) => {
    if (checked) {
      onChange([...(selected || []).filter((item) => availableIds.has(item.id)), { ...addon, active: true, dayNumber: null }]);
    } else {
      onChange((selected || []).filter((item) => item.id !== addon.id && availableIds.has(item.id)));
    }
  };
  // The package day the add-on usually happens on; the driver's message dates it from the travel date.
  const selectedDay = (addonId) => (selected || []).find((item) => item.id === addonId)?.dayNumber ?? "";
  const setDay = (addonId, value) => {
    onChange((selected || [])
      .filter((item) => availableIds.has(item.id))
      .map((item) => (item.id === addonId ? { ...item, dayNumber: value === "" ? null : Number(value) } : item)));
  };

  if (!available.length) {
    return <div className="text-muted">No add-ons found. Create add-ons from the Add-ons menu first.</div>;
  }

  return (
    <div className="d-flex flex-column gap-2">
      {available.map((addon) => (
        <Card key={addon.id} className="border">
          <Card.Body className="py-2">
            <Form.Check
              type="checkbox"
              id={`trip-addon-${addon.id}`}
              checked={selectedIds.has(addon.id)}
              onChange={(e) => toggle(addon, e.target.checked)}
              label={
                <span>
                  <span className="fw-semibold">{addon.name}</span>
                  <span className="text-muted small ms-2">
                    Rs. {Number(addon.basePrice || 0).toLocaleString("en-IN")}
                  </span>
                </span>
              }
            />
            {addon.description && <div className="text-muted small ms-4">{addon.description}</div>}
            {selectedIds.has(addon.id) && (
              <div className="d-flex align-items-center gap-2 ms-4 mt-1 small">
                <span className="text-muted">Usually on package day</span>
                <Form.Control
                  size="sm"
                  type="number"
                  min="1"
                  step="1"
                  style={{ width: 80 }}
                  placeholder="Day"
                  value={selectedDay(addon.id)}
                  onChange={(e) => setDay(addon.id, e.target.value)}
                />
                <span className="text-muted">Staff can change the date per booking.</span>
              </div>
            )}
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}

// E-passes the vehicle needs on this trip, each on the package day it is needed. Every booking of
// the trip then tracks it in Trip Preparation, including bookings made before it was set here.
function TripEpassSelector({ selected, available, durationDays, onChange }) {
  const selectedById = new Map((selected || []).map((epass) => [String(epass.id), epass]));
  // A switched-off e-pass stays listed only while this trip still uses it, so it can be unticked.
  const shown = (available || []).filter((epass) => epass.active !== false || selectedById.has(String(epass.id)));
  const toggle = (epass, checked) => {
    const rest = (selected || []).filter((item) => String(item.id) !== String(epass.id));
    onChange(checked ? [...rest, { ...epass, dayNumber: null }] : rest);
  };
  const setDay = (epassId, value) => {
    onChange((selected || []).map((item) =>
      String(item.id) === String(epassId) ? { ...item, dayNumber: value === "" ? null : Number(value) } : item));
  };

  if (!shown.length) {
    return <div className="text-muted">No e-passes found. Create them from the E-Passes menu first.</div>;
  }

  return (
    <div className="d-flex flex-column gap-2">
      {shown.map((epass) => {
        const chosen = selectedById.get(String(epass.id));
        const day = chosen?.dayNumber ?? "";
        const dayMissing = chosen && !(Number(day) >= 1);
        return (
          <Card key={epass.id} className="border">
            <Card.Body className="py-2">
              <Form.Check
                type="checkbox"
                id={`trip-epass-${epass.id}`}
                checked={Boolean(chosen)}
                onChange={(e) => toggle(epass, e.target.checked)}
                label={<span className="fw-semibold">{epass.name}{epass.active === false ? " (switched off)" : ""}</span>}
              />
              {epass.instructions && <div className="text-muted small ms-4" style={{ whiteSpace: "pre-line" }}>{epass.instructions}</div>}
              {chosen && (
                <div className="d-flex align-items-center gap-2 ms-4 mt-1 small">
                  <span className="text-muted">Needed on package day</span>
                  <Form.Select
                    size="sm"
                    style={{ width: 100 }}
                    value={day}
                    isInvalid={dayMissing}
                    onChange={(e) => setDay(epass.id, e.target.value)}
                  >
                    <option value="">Day</option>
                    {Array.from({ length: Math.max(1, durationDays) }, (_, index) => index + 1).map((value) => (
                      <option key={value} value={value}>Day {value}</option>
                    ))}
                  </Form.Select>
                  <span className={dayMissing ? "text-danger" : "text-muted"}>
                    {dayMissing ? "Choose the day before saving." : "Staff can change the date per booking."}
                  </span>
                </div>
              )}
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
}
